/**
 * deposito-tabs.ts
 * The Depósito section's tabs and the capability each one needs
 * (odd/tasks/compras-deposito.md S1). Shared by DepositoLayout.vue (which tabs
 * to show), the routes (meta.requiresCapability) and the home view (which
 * action tiles to show), so the three never disagree. The capabilities are
 * exactly the ones the old /servicios/deposito, /servicios/stock and
 * /compras/stock screens asked for — none added or removed. The same tabs are
 * mounted under /compras/deposito and /servicios/deposito (deposito-base.ts).
 */
import type { DepositoPage } from './deposito-base.js';

/** Anyone who could read stock on either old screen (Compras or Servicios). */
export const DEPOSITO_READ_CAPABILITIES = ['locx.stock.leer', 'requi.stock.leer'] as const;
export const DEPOSITO_RECEIVE_CAPABILITY = 'locx.stock.recepcionar';
export const DEPOSITO_DISPATCH_CAPABILITY = 'locx.stock.despachar';
/** The Movimientos tab keeps the old screen's locx.stock.leer (the endpoint also accepts requi.stock.leer). */
export const DEPOSITO_MOVEMENTS_CAPABILITY = 'locx.stock.leer';
/** The item view (history + consumo/ajuste/conteo) is Compras' own requi.stock.leer screen. */
export const DEPOSITO_ITEM_CAPABILITY = 'requi.stock.leer';
/** Transfers and returns (odd/tasks/compras-deposito-mover.md S3): who delivers or who adjusts — no new capability. */
export const DEPOSITO_MOVE_CAPABILITIES = ['locx.stock.despachar', 'requi.stock.ajustar'] as const;
/** The cyclic count: the Contar tab (bulk count + initial load) and the action inside the item view. */
export const DEPOSITO_COUNT_CAPABILITY = 'requi.stock.ajustar';

export interface DepositoTab {
  /** Tab value and page key; the route name comes from depositoRouteName(base, value). */
  value: Exclude<DepositoPage, 'item'>;
  label: string;
  /** Child path under the base (`''` is the home). */
  path: string;
  requiredCapability: string | readonly string[];
}

export const DEPOSITO_TABS: readonly DepositoTab[] = [
  { value: 'inicio', label: 'Inicio', path: '', requiredCapability: DEPOSITO_READ_CAPABILITIES },
  { value: 'recibir', label: 'Recibir', path: 'recibir', requiredCapability: DEPOSITO_RECEIVE_CAPABILITY },
  { value: 'entregar', label: 'Entregar', path: 'entregar', requiredCapability: DEPOSITO_DISPATCH_CAPABILITY },
  { value: 'contar', label: 'Contar', path: 'contar', requiredCapability: DEPOSITO_COUNT_CAPABILITY },
  { value: 'mover', label: 'Mover', path: 'mover', requiredCapability: DEPOSITO_MOVE_CAPABILITIES },
  { value: 'movimientos', label: 'Movimientos', path: 'movimientos', requiredCapability: DEPOSITO_MOVEMENTS_CAPABILITY },
];

/** "Pedir compra" on a below-minimum article creates an SA: the capability of POST /procurement/cases. */
export const REQUEST_PURCHASE_CAPABILITY = 'requi.solicitud.crear';
